import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { 
    ArrowLeft, 
    Users, 
    Trash2, 
    Shield, 
    User, 
    Loader2,
    BookOpen,
    RefreshCw,
    Eye,
    CheckCircle,
    XCircle,
    Clock,
    UserCheck,
    UserX
} from 'lucide-react';

const HERO_BG = "https://customer-assets.emergentagent.com/job_gamma-to-web/artifacts/brgxn5xe_Grok%20Image%202026-02-05%20at%2017.41.51.png";
const API_URL = process.env.REACT_APP_BACKEND_URL;

const StatusBadge = ({ status }) => {
    const configs = {
        pending: { 
            icon: Clock, 
            text: 'Pending', 
            className: 'bg-amber-500/20 text-amber-500 border-amber-500/30' 
        },
        active: { 
            icon: CheckCircle, 
            text: 'Active', 
            className: 'bg-success/20 text-success border-success/30' 
        },
        suspended: { 
            icon: XCircle, 
            text: 'Suspended', 
            className: 'bg-destructive/20 text-destructive border-destructive/30' 
        }
    };
    
    const config = configs[status] || configs.pending;
    const Icon = config.icon;
    
    return (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full border font-ui ${config.className}`}>
            <Icon className="w-3 h-3" />
            {config.text}
        </span>
    );
};

const AdminPage = () => {
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [actionLoading, setActionLoading] = useState(null);
    const [selectedUser, setSelectedUser] = useState(null);
    const [filter, setFilter] = useState('all'); // 'all', 'pending', 'active', 'suspended'

    const fetchUsers = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(`${API_URL}/api/admin/users`, {
                credentials: 'include'
            });
            
            if (!response.ok) {
                throw new Error('Failed to fetch users');
            }
            
            const data = await response.json();
            setUsers(data.users || []);
        } catch (err) {
            console.error('Error fetching users:', err);
            setError('Failed to load users. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    const updateUserStatus = async (userId, newStatus, userName) => {
        const action = newStatus === 'active' ? 'approve' : newStatus === 'suspended' ? 'suspend' : 'set to pending';
        if (!window.confirm(`Are you sure you want to ${action} ${userName}?`)) {
            return;
        }
        
        setActionLoading(userId);
        try {
            const response = await fetch(`${API_URL}/api/admin/users/${userId}/status`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ status: newStatus })
            });
            
            if (!response.ok) {
                throw new Error('Failed to update status');
            }
            
            // Update user in list
            setUsers(prev => prev.map(u => 
                u.user_id === userId ? { ...u, status: newStatus } : u
            ));
            
            // Update selected user if viewing
            if (selectedUser?.user?.user_id === userId) {
                setSelectedUser(prev => ({
                    ...prev,
                    user: { ...prev.user, status: newStatus }
                }));
            }
        } catch (err) {
            console.error('Error updating status:', err);
            alert('Failed to update user status. Please try again.');
        } finally {
            setActionLoading(null);
        }
    };

    const handleDeleteUser = async (userId, userName) => {
        if (!window.confirm(`Are you sure you want to delete ${userName}? This action cannot be undone.`)) {
            return;
        }
        
        setActionLoading(userId);
        try {
            const response = await fetch(`${API_URL}/api/admin/users/${userId}`, {
                method: 'DELETE',
                credentials: 'include'
            });
            
            if (!response.ok) {
                throw new Error('Failed to delete user');
            }
            
            // Remove user from list
            setUsers(prev => prev.filter(u => u.user_id !== userId));
            if (selectedUser?.user?.user_id === userId) {
                setSelectedUser(null);
            }
        } catch (err) {
            console.error('Error deleting user:', err);
            alert('Failed to delete user. Please try again.');
        } finally {
            setActionLoading(null);
        }
    };

    const viewUserDetails = async (userId) => {
        try {
            const response = await fetch(`${API_URL}/api/admin/users/${userId}`, {
                credentials: 'include'
            });
            
            if (!response.ok) {
                throw new Error('Failed to fetch user details');
            }
            
            const data = await response.json();
            setSelectedUser(data);
        } catch (err) {
            console.error('Error fetching user details:', err);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        return new Date(dateStr).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handleLogout = async () => {
        await logout();
        navigate('/login');
    };

    // Filter users
    const filteredUsers = users.filter(u => {
        if (filter === 'all') return true;
        return u.status === filter;
    });

    // Count by status
    const counts = {
        all: users.length,
        pending: users.filter(u => u.status === 'pending').length,
        active: users.filter(u => u.status === 'active').length,
        suspended: users.filter(u => u.status === 'suspended').length
    };

    return (
        <div className="min-h-screen relative">
            {/* Background */}
            <div 
                className="fixed inset-0 bg-cover bg-center bg-no-repeat -z-10"
                style={{ backgroundImage: `url(${HERO_BG})` }}
            />
            <div className="fixed inset-0 bg-background/85 backdrop-blur-[2px] -z-10" />

            {/* Header */}
            <header className="sticky top-0 z-50 bg-card/80 backdrop-blur-md border-b border-border/50">
                <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate('/dashboard')}
                            className="flex items-center gap-2"
                            data-testid="back-to-dashboard-btn"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            Back to Dashboard
                        </Button>
                        <div className="flex items-center gap-2">
                            <Shield className="w-5 h-5 text-primary" />
                            <h1 className="font-display text-xl font-bold text-foreground">
                                Admin Panel
                            </h1>
                        </div>
                    </div>
                    <div className="flex items-center gap-4">
                        <span className="text-sm text-muted-foreground font-ui hidden sm:block">
                            {user?.email}
                        </span>
                        <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="admin-logout-btn">
                            Sign Out
                        </Button>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="max-w-7xl mx-auto px-4 py-8">
                {/* Filter Tabs */}
                <div className="flex flex-wrap gap-2 mb-6">
                    {[
                        { key: 'all', label: 'All Users' },
                        { key: 'pending', label: 'Pending Approval' },
                        { key: 'active', label: 'Active' },
                        { key: 'suspended', label: 'Suspended' }
                    ].map(tab => (
                        <Button
                            key={tab.key}
                            variant={filter === tab.key ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setFilter(tab.key)}
                            className="gap-2"
                            data-testid={`filter-${tab.key}-btn`}
                        >
                            {tab.label}
                            <span className={`px-1.5 py-0.5 text-xs rounded-full ${
                                filter === tab.key 
                                    ? 'bg-white/20' 
                                    : 'bg-muted'
                            }`}>
                                {counts[tab.key]}
                            </span>
                        </Button>
                    ))}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Users List */}
                    <div className="lg:col-span-2">
                        <Card className="bg-card/80 backdrop-blur-sm border-border/50">
                            <CardHeader className="flex flex-row items-center justify-between">
                                <div>
                                    <CardTitle className="flex items-center gap-2">
                                        <Users className="w-5 h-5 text-primary" />
                                        {filter === 'pending' ? 'Pending Approvals' : 
                                         filter === 'active' ? 'Active Users' :
                                         filter === 'suspended' ? 'Suspended Users' : 'All Users'}
                                    </CardTitle>
                                    <CardDescription>
                                        {filter === 'pending' 
                                            ? 'Review and approve new registrations' 
                                            : 'Manage user accounts and access'}
                                    </CardDescription>
                                </div>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={fetchUsers}
                                    disabled={loading}
                                    data-testid="refresh-users-btn"
                                >
                                    <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                                    Refresh
                                </Button>
                            </CardHeader>
                            <CardContent>
                                {loading ? (
                                    <div className="flex items-center justify-center py-12">
                                        <Loader2 className="w-8 h-8 text-primary animate-spin" />
                                    </div>
                                ) : error ? (
                                    <div className="text-center py-12">
                                        <p className="text-destructive mb-4">{error}</p>
                                        <Button onClick={fetchUsers}>Try Again</Button>
                                    </div>
                                ) : filteredUsers.length === 0 ? (
                                    <div className="text-center py-12 text-muted-foreground">
                                        {filter === 'pending' 
                                            ? 'No pending approvals' 
                                            : `No ${filter} users found`}
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        {filteredUsers.map((u) => (
                                            <div
                                                key={u.user_id}
                                                data-testid={`user-row-${u.user_id}`}
                                                className={`p-4 rounded-lg border transition-all ${
                                                    selectedUser?.user?.user_id === u.user_id 
                                                        ? 'border-primary bg-primary/5' 
                                                        : 'border-border/50 hover:border-primary/30 bg-card/50'
                                                } ${u.status === 'pending' ? 'ring-2 ring-amber-500/20' : ''}`}
                                            >
                                                <div className="flex items-center justify-between gap-4">
                                                    <div 
                                                        className="flex items-center gap-4 flex-1 cursor-pointer"
                                                        onClick={() => viewUserDetails(u.user_id)}
                                                    >
                                                        {u.picture ? (
                                                            <img 
                                                                src={u.picture} 
                                                                alt={u.name}
                                                                className="w-10 h-10 rounded-full object-cover"
                                                            />
                                                        ) : (
                                                            <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                                                                <User className="w-5 h-5 text-primary" />
                                                            </div>
                                                        )}
                                                        <div className="min-w-0">
                                                            <div className="flex items-center gap-2 flex-wrap">
                                                                <p className="font-medium text-foreground truncate">
                                                                    {u.name}
                                                                </p>
                                                                {u.role === 'admin' && (
                                                                    <span className="px-2 py-0.5 text-xs rounded-full bg-primary/20 text-primary font-ui">
                                                                        Admin
                                                                    </span>
                                                                )}
                                                                <StatusBadge status={u.status} />
                                                            </div>
                                                            <p className="text-sm text-muted-foreground truncate">
                                                                {u.email}
                                                            </p>
                                                            <p className="text-xs text-muted-foreground mt-1">
                                                                {u.auth_provider === 'google' ? 'Google' : 'Email'} • {u.completed_lessons_count || 0} lessons
                                                            </p>
                                                        </div>
                                                    </div>
                                                    
                                                    {/* Action Buttons */}
                                                    {u.role !== 'admin' && (
                                                        <div className="flex items-center gap-2 flex-shrink-0">
                                                            {u.status === 'pending' && (
                                                                <Button
                                                                    variant="default"
                                                                    size="sm"
                                                                    className="gap-1 bg-success hover:bg-success/90"
                                                                    onClick={() => updateUserStatus(u.user_id, 'active', u.name)}
                                                                    disabled={actionLoading === u.user_id}
                                                                    data-testid={`approve-${u.user_id}-btn`}
                                                                >
                                                                    {actionLoading === u.user_id ? (
                                                                        <Loader2 className="w-4 h-4 animate-spin" />
                                                                    ) : (
                                                                        <UserCheck className="w-4 h-4" />
                                                                    )}
                                                                    <span className="hidden sm:inline">Approve</span>
                                                                </Button>
                                                            )}
                                                            {u.status === 'active' && (
                                                                <Button
                                                                    variant="outline"
                                                                    size="sm"
                                                                    className="gap-1 text-destructive hover:text-destructive hover:bg-destructive/10"
                                                                    onClick={() => updateUserStatus(u.user_id, 'suspended', u.name)}
                                                                    disabled={actionLoading === u.user_id}
                                                                    data-testid={`suspend-${u.user_id}-btn`}
                                                                >
                                                                    {actionLoading === u.user_id ? (
                                                                        <Loader2 className="w-4 h-4 animate-spin" />
                                                                    ) : (
                                                                        <UserX className="w-4 h-4" />
                                                                    )}
                                                                    <span className="hidden sm:inline">Suspend</span>
                                                                </Button>
                                                            )}
                                                            {u.status === 'suspended' && (
                                                                <Button
                                                                    variant="outline"
                                                                    size="sm"
                                                                    className="gap-1"
                                                                    onClick={() => updateUserStatus(u.user_id, 'active', u.name)}
                                                                    disabled={actionLoading === u.user_id}
                                                                    data-testid={`reactivate-${u.user_id}-btn`}
                                                                >
                                                                    {actionLoading === u.user_id ? (
                                                                        <Loader2 className="w-4 h-4 animate-spin" />
                                                                    ) : (
                                                                        <UserCheck className="w-4 h-4" />
                                                                    )}
                                                                    <span className="hidden sm:inline">Reactivate</span>
                                                                </Button>
                                                            )}
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() => viewUserDetails(u.user_id)}
                                                                data-testid={`view-${u.user_id}-btn`}
                                                            >
                                                                <Eye className="w-4 h-4" />
                                                            </Button>
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                                                onClick={() => handleDeleteUser(u.user_id, u.name)}
                                                                disabled={actionLoading === u.user_id}
                                                                data-testid={`delete-${u.user_id}-btn`}
                                                            >
                                                                {actionLoading === u.user_id ? (
                                                                    <Loader2 className="w-4 h-4 animate-spin" />
                                                                ) : (
                                                                    <Trash2 className="w-4 h-4" />
                                                                )}
                                                            </Button>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </div>

                    {/* User Detail Panel */}
                    <div>
                        <Card className="bg-card/80 backdrop-blur-sm border-border/50 sticky top-24">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <BookOpen className="w-5 h-5 text-primary" />
                                    User Details
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                {selectedUser ? (
                                    <div className="space-y-4">
                                        <div className="flex items-center gap-4 pb-4 border-b border-border/50">
                                            {selectedUser.user?.picture ? (
                                                <img 
                                                    src={selectedUser.user.picture} 
                                                    alt={selectedUser.user.name}
                                                    className="w-16 h-16 rounded-full object-cover"
                                                />
                                            ) : (
                                                <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center">
                                                    <User className="w-8 h-8 text-primary" />
                                                </div>
                                            )}
                                            <div>
                                                <p className="font-display font-bold text-foreground">
                                                    {selectedUser.user?.name}
                                                </p>
                                                <p className="text-sm text-muted-foreground">
                                                    {selectedUser.user?.email}
                                                </p>
                                                <div className="mt-1">
                                                    <StatusBadge status={selectedUser.user?.status} />
                                                </div>
                                            </div>
                                        </div>
                                        
                                        <div className="space-y-3">
                                            <div>
                                                <p className="text-xs text-muted-foreground font-ui uppercase tracking-wider">
                                                    Role
                                                </p>
                                                <p className="text-sm font-medium text-foreground capitalize">
                                                    {selectedUser.user?.role || 'User'}
                                                </p>
                                            </div>
                                            <div>
                                                <p className="text-xs text-muted-foreground font-ui uppercase tracking-wider">
                                                    Auth Provider
                                                </p>
                                                <p className="text-sm font-medium text-foreground capitalize">
                                                    {selectedUser.user?.auth_provider || 'Email'}
                                                </p>
                                            </div>
                                            <div>
                                                <p className="text-xs text-muted-foreground font-ui uppercase tracking-wider">
                                                    Joined
                                                </p>
                                                <p className="text-sm font-medium text-foreground">
                                                    {formatDate(selectedUser.user?.created_at)}
                                                </p>
                                            </div>
                                            <div>
                                                <p className="text-xs text-muted-foreground font-ui uppercase tracking-wider">
                                                    Lessons Completed
                                                </p>
                                                <p className="text-sm font-medium text-foreground">
                                                    {selectedUser.progress?.completed_lessons?.length || 0} / 68
                                                </p>
                                            </div>
                                            <div>
                                                <p className="text-xs text-muted-foreground font-ui uppercase tracking-wider">
                                                    Last Progress Update
                                                </p>
                                                <p className="text-sm font-medium text-foreground">
                                                    {formatDate(selectedUser.progress?.updated_at)}
                                                </p>
                                            </div>
                                        </div>

                                        {selectedUser.progress?.completed_lessons?.length > 0 && (
                                            <div className="pt-4 border-t border-border/50">
                                                <p className="text-xs text-muted-foreground font-ui uppercase tracking-wider mb-2">
                                                    Completed Lessons
                                                </p>
                                                <div className="max-h-40 overflow-y-auto">
                                                    <div className="flex flex-wrap gap-1">
                                                        {selectedUser.progress.completed_lessons.map((lessonId) => (
                                                            <span
                                                                key={lessonId}
                                                                className="px-2 py-0.5 text-xs rounded bg-success/20 text-success font-mono"
                                                            >
                                                                {lessonId.replace('lesson-', '')}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="text-center py-8 text-muted-foreground">
                                        <User className="w-12 h-12 mx-auto mb-3 opacity-50" />
                                        <p className="text-sm">Select a user to view details</p>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </div>
                </div>

                {/* Stats Summary */}
                <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card className="bg-card/80 backdrop-blur-sm border-border/50">
                        <CardContent className="pt-6">
                            <div className="flex items-center gap-4">
                                <div className="p-3 rounded-lg bg-primary/20">
                                    <Users className="w-6 h-6 text-primary" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold text-foreground">{counts.all}</p>
                                    <p className="text-sm text-muted-foreground">Total Users</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="bg-card/80 backdrop-blur-sm border-border/50">
                        <CardContent className="pt-6">
                            <div className="flex items-center gap-4">
                                <div className="p-3 rounded-lg bg-amber-500/20">
                                    <Clock className="w-6 h-6 text-amber-500" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold text-foreground">{counts.pending}</p>
                                    <p className="text-sm text-muted-foreground">Pending</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="bg-card/80 backdrop-blur-sm border-border/50">
                        <CardContent className="pt-6">
                            <div className="flex items-center gap-4">
                                <div className="p-3 rounded-lg bg-success/20">
                                    <CheckCircle className="w-6 h-6 text-success" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold text-foreground">{counts.active}</p>
                                    <p className="text-sm text-muted-foreground">Active</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="bg-card/80 backdrop-blur-sm border-border/50">
                        <CardContent className="pt-6">
                            <div className="flex items-center gap-4">
                                <div className="p-3 rounded-lg bg-destructive/20">
                                    <XCircle className="w-6 h-6 text-destructive" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold text-foreground">{counts.suspended}</p>
                                    <p className="text-sm text-muted-foreground">Suspended</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </main>

            {/* Footer */}
            <footer className="py-6 text-center text-sm text-muted-foreground font-ui">
                &copy; {new Date().getFullYear()} Gringos English. All rights reserved.
            </footer>
        </div>
    );
};

export default AdminPage;
